/**
 * Tedarikçi alım listesi (supplier_sessions) cari bakiye hesapları.
 * remaining = toplam alım - ödenen (negatif → tedarikçi bize borçlu / fazla ödeme)
 */
export const getSupplierSessionRemaining = (session) => {
  const total = parseFloat(session?.totalOrderAmount || 0) || 0;
  const paid = parseFloat(session?.paidAmount || 0) || 0;
  return total - paid;
};

export const formatSupplierBalanceLabel = (remaining) => {
  if (remaining < -0.009) {
    return { type: 'credit', label: 'Tedarikçi Bize Borçlu', amount: Math.abs(remaining) };
  }
  if (remaining > 0.009) {
    return { type: 'debt', label: 'Kalan Borcumuz', amount: remaining };
  }
  return { type: 'balanced', label: 'Dengede', amount: 0 };
};

export const aggregateSupplierSessionsByCurrency = (sessions = [], supplierDirectPayments = []) => {
  const result = (sessions || []).reduce((acc, session) => {
    const currency = session.currency || 'TRY';
    if (!acc[currency]) {
      acc[currency] = { total: 0, sessionPaid: 0, directPaid: 0, paid: 0, netBalance: 0, count: 0 };
    }
    const total = parseFloat(session.totalOrderAmount || 0) || 0;
    const paid = parseFloat(session.paidAmount || 0) || 0;

    acc[currency].total += total;
    acc[currency].sessionPaid += paid;
    acc[currency].count += 1;
    return acc;
  }, {});

  const directList = Array.isArray(supplierDirectPayments) ? supplierDirectPayments : [];
  directList.forEach((payment) => {
    const currency = payment.currency || 'TRY';
    if (!result[currency]) {
      result[currency] = { total: 0, sessionPaid: 0, directPaid: 0, paid: 0, netBalance: 0, count: 0 };
    }
    const amount = parseFloat(payment.amount || 0) || 0;
    result[currency].directPaid += amount;
  });

  // Toplam ödenen ve net bakiye hesapla
  Object.keys(result).forEach((currency) => {
    const group = result[currency];
    group.paid = group.sessionPaid + group.directPaid;
    group.netBalance = group.total - group.paid;
  });

  return result;
};

export const aggregateSupplierSessionBalances = (sessions = [], exchangeRates = {}, supplierDirectPayments = []) => {
  const currencyGroups = aggregateSupplierSessionsByCurrency(sessions, supplierDirectPayments);

  const currencyLines = Object.entries(currencyGroups).map(([currency, data]) => ({
    currency,
    sessionCount: data.count,
    total: data.total,
    sessionPaid: data.sessionPaid,
    directPaid: data.directPaid,
    paid: data.paid,
    netBalance: data.netBalance,
    openBalance: Math.max(0, data.netBalance),
    creditBalance: Math.max(0, -data.netBalance),
    isCreditor: data.netBalance < -0.009,
    isBalanced: Math.abs(data.netBalance) < 0.01
  }));

  const totalNetTry = currencyLines.reduce((sum, line) => {
    if (line.currency === 'TRY') return sum + line.netBalance;
    const rate = parseFloat(exchangeRates[line.currency]) || 1;
    return sum + line.netBalance * rate;
  }, 0);

  const totalDebtTry = currencyLines.reduce((sum, line) => {
    if (line.currency === 'TRY') return sum + line.openBalance;
    const rate = parseFloat(exchangeRates[line.currency]) || 1;
    return sum + line.openBalance * rate;
  }, 0);

  const totalCreditTry = currencyLines.reduce((sum, line) => {
    if (line.currency === 'TRY') return sum + line.creditBalance;
    const rate = parseFloat(exchangeRates[line.currency]) || 1;
    return sum + line.creditBalance * rate;
  }, 0);

  const totalPaidTry = currencyLines.reduce((sum, line) => {
    if (line.currency === 'TRY') return sum + line.paid;
    const rate = parseFloat(exchangeRates[line.currency]) || 1;
    return sum + line.paid * rate;
  }, 0);

  const totalPurchasesTry = currencyLines.reduce((sum, line) => {
    if (line.currency === 'TRY') return sum + line.total;
    const rate = parseFloat(exchangeRates[line.currency]) || 1;
    return sum + line.total * rate;
  }, 0);

  return {
    currencyLines,
    totalNetTry,
    totalDebtTry,
    totalCreditTry,
    totalPaidTry,
    totalPurchasesTry,
    openBalance: Math.max(0, totalNetTry),
    creditBalance: Math.max(0, -totalNetTry),
    isCreditor: totalNetTry < -0.009,
    isBalanced: Math.abs(totalNetTry) < 0.01
  };
};
